import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  endCurrentImpersonation as apiEndImpersonation,
  getCurrentImpersonationSession as apiGetCurrentImpersonation,
  startImpersonation as apiStartImpersonation,
  type ImpersonationSession,
} from '@/api/platform/tenants'
import { useAuthStore } from '@/stores/auth'

/**
 * Impersonation store (S-SUP-050).
 *
 * Owns the session-banner state. Kept separate from `authStore` so:
 *   - the banner can be rendered without pulling in the rest of auth semantics
 *   - ending a session is idempotent: if the server has already expired the
 *     session, we still clear local state.
 *
 * The session access token supplied by the server is layered over the
 * Super-Admin token via `authStore.accessToken`. When the session ends we
 * restore the pre-impersonation token we tucked away in `preImpersonationToken`
 * on start so the caller goes straight back to Super Admin posture without
 * waiting for a server round-trip.
 */
export const useImpersonationStore = defineStore('impersonation', () => {
  const session = ref<ImpersonationSession | null>(null)
  const endingSession = ref(false)
  /** Super Admin token captured at `start()` — restored on `end()`. */
  const preImpersonationToken = ref<string | null>(null)

  const isActive = computed(() => session.value !== null)

  /** Seconds remaining until the server-issued session expires. Negative when
   *  past-due; the banner should highlight and auto-end on 0. */
  const secondsRemaining = ref<number>(0)
  let tickTimer: ReturnType<typeof setInterval> | null = null

  function computeRemaining(): void {
    if (!session.value) {
      secondsRemaining.value = 0
      return
    }
    secondsRemaining.value = Math.floor(
      (new Date(session.value.expiresAt).getTime() - Date.now()) / 1000,
    )
  }

  function startTicker(): void {
    stopTicker()
    computeRemaining()
    tickTimer = setInterval(() => {
      if (!session.value) return
      computeRemaining()
    }, 1000)
  }

  function stopTicker(): void {
    if (tickTimer) {
      clearInterval(tickTimer)
      tickTimer = null
    }
  }

  /**
   * Called once on app mount (from `bootstrap()` in main.ts). Queries the
   * server for the caller's live session; if one exists, re-seeds the banner
   * so a hard refresh doesn't lose the impersonation posture.
   */
  async function hydrate(): Promise<void> {
    try {
      const current = await apiGetCurrentImpersonation()
      if (current) {
        session.value = current
        const auth = useAuthStore()
        auth.activeTenantId = current.tenantId
        auth.isImpersonating = true
        startTicker()
      }
    } catch {
      // Swallow — a dead server on app-mount shouldn't block the SPA from
      // painting; the banner will simply be absent until reload.
    }
  }

  async function start(tenantId: string, reason: string): Promise<ImpersonationSession> {
    const auth = useAuthStore()
    preImpersonationToken.value = auth.accessToken
    const result = await apiStartImpersonation(tenantId, reason)
    session.value = result
    // Swap the in-memory bearer token so subsequent axios calls run scoped.
    if (result.accessToken) {
      auth.accessToken = result.accessToken
    }
    auth.activeTenantId = result.tenantId
    auth.isImpersonating = true
    startTicker()
    return result
  }

  async function end(): Promise<void> {
    if (!session.value || endingSession.value) return
    endingSession.value = true
    const endedTenantId = session.value.tenantId
    try {
      try {
        await apiEndImpersonation()
      } catch {
        // Continue tear-down even if the server call fails — the banner must
        // go away; the backend will reconcile on the next refresh.
      }
      session.value = null
      stopTicker()
      const auth = useAuthStore()
      auth.isImpersonating = false
      auth.activeTenantId = null
      if (preImpersonationToken.value) {
        // Fast path — re-seat the Super Admin token we captured on start.
        auth.accessToken = preImpersonationToken.value
        preImpersonationToken.value = null
      } else {
        // Fallback: reacquire a fresh token from the HttpOnly refresh cookie.
        await auth.refresh()
      }
      // Returned for callers that want to push `/platform/tenants/:id` —
      // stashed on the store for components that consume via getter.
      lastEndedTenantId.value = endedTenantId
    } finally {
      endingSession.value = false
    }
  }

  const lastEndedTenantId = ref<string | null>(null)

  function clear(): void {
    session.value = null
    secondsRemaining.value = 0
    preImpersonationToken.value = null
    stopTicker()
  }

  return {
    session,
    isActive,
    secondsRemaining,
    endingSession,
    preImpersonationToken,
    lastEndedTenantId,
    hydrate,
    start,
    end,
    clear,
  }
})
